import type { Blame, FailureClass, PublisherStatus, PublishingTone } from '@schemas/error-pages'
import {
  BlameEnum,
  ErrorVariantEnum,
  FAILURE_CLASS_COPY,
  FailureClassEnum,
  PublisherStatusStateEnum,
  PublishingToneEnum
} from '@schemas/error-pages'
import { m } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { fadeSlideUp } from '../../lib/motion.ts'
import { Badge } from '../badge.tsx'
import { Button } from '../button.tsx'
import { Card, CardBody } from '../card.tsx'

const TONE_DOT: Record<PublishingTone, string> = {
  ok: 'bg-signal-ok',
  fail: 'bg-signal-fail',
  waiting: 'bg-signal-waiting'
}
const TONE_TEXT: Record<PublishingTone, string> = {
  ok: 'text-signal-ok',
  fail: 'text-signal-fail',
  waiting: 'text-signal-waiting'
}
const TONE_BG: Record<PublishingTone, string> = {
  ok: 'bg-signal-ok-bg',
  fail: 'bg-signal-fail-bg',
  waiting: 'bg-signal-waiting-bg'
}
const TONE_BORDER: Record<PublishingTone, string> = {
  ok: 'border-signal-ok-border',
  fail: 'border-signal-fail-border',
  waiting: 'border-signal-waiting-border'
}

const COPY_RESET_MS = 2000

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  return {
    copied,
    async copy(value: string) {
      try {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => setCopied(false), COPY_RESET_MS)
      } catch {
        // Clipboard permission denied/unavailable — the reference stays visible and
        // selectable on the page either way, so there's nothing to recover here.
      }
    }
  }
}

function ReferenceLine({ reference }: { reference: string }) {
  const { copied, copy } = useCopyToClipboard()
  return (
    <div className="flex flex-wrap items-center gap-4">
      <span className="font-mono text-[11px] text-ink-secondary">
        Reference <span className="text-ink-soft">{reference}</span> · quote this and we can find it
      </span>
      <button
        type="button"
        onClick={() => copy(reference)}
        className="font-mono text-[10px] font-medium uppercase tracking-[0.11em] text-signal-oxide hover:text-signal-oxide-bright"
      >
        {copied ? 'Copied' : 'Copy reference'}
      </button>
    </div>
  )
}

function BlameBadge({ blame }: { blame: Blame }) {
  return (
    <Badge variant={blame === BlameEnum.enum.rally ? 'danger' : 'neutral'}>
      {blame === BlameEnum.enum.rally ? "Rally's fault" : 'TikTok'}
    </Badge>
  )
}

// RAL-163's unresolved publisher-status dependency: no source independent of the failing path
// exists yet (tracked as backend follow-up). Every current call site passes `state: 'unknown'`,
// which renders this panel instead of trusting a failure class's default assumption against
// unconfirmed live status — silence beats a comforting guess.
function PublishingStatus({
  failureClass,
  publisherStatus
}: {
  failureClass: FailureClass
  publisherStatus: PublisherStatus
}) {
  const copy = FAILURE_CLASS_COPY[failureClass]

  if (publisherStatus.state === PublisherStatusStateEnum.enum.unknown) {
    return (
      <div className={`border p-4 ${TONE_BG.waiting} ${TONE_BORDER.waiting}`}>
        <div className="mb-2 flex items-center gap-2">
          <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 ${TONE_DOT.waiting}`} />
          <span className={`font-mono text-[10px] font-medium uppercase tracking-[0.11em] ${TONE_TEXT.waiting}`}>
            We can't tell from here
          </span>
        </div>
        <p className="text-xs leading-relaxed text-ink-soft">
          Publisher status couldn't be confirmed independently of this failure. We won't guess whether Rally is still
          publishing.
        </p>
      </div>
    )
  }

  if (publisherStatus.columns) {
    const cols: Array<{ tone: PublishingTone; label: string; body: string }> = [
      { tone: PublishingToneEnum.enum.ok, label: 'Still running', body: publisherStatus.columns.stillRunning },
      { tone: PublishingToneEnum.enum.fail, label: 'Stopped', body: publisherStatus.columns.stopped },
      { tone: PublishingToneEnum.enum.waiting, label: 'Untouched', body: publisherStatus.columns.untouched }
    ]
    return (
      <div className="grid gap-px overflow-hidden border border-border-hairline bg-border-hairline sm:grid-cols-3">
        {cols.map((col) => (
          <div key={col.label} className="bg-ground-surface p-4">
            <div className="mb-2 flex items-center gap-2">
              <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 ${TONE_DOT[col.tone]}`} />
              <span className={`font-mono text-[10px] font-medium uppercase tracking-[0.11em] ${TONE_TEXT[col.tone]}`}>
                {col.label}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-ink-soft">{col.body}</p>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={`border p-4 ${TONE_BG[copy.publishingTone]}`}>
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 ${TONE_DOT[copy.publishingTone]}`} />
        <span
          className={`font-mono text-[10px] font-medium uppercase tracking-[0.11em] ${TONE_TEXT[copy.publishingTone]}`}
        >
          Publishing: {copy.publishingSummary}
        </span>
      </div>
    </div>
  )
}

interface ShellFrameProps {
  failureClass: FailureClass
  publisherStatus: PublisherStatus
  reference: string
  onRetry?: () => void
}

function ShellFrame({ failureClass, publisherStatus, reference, onRetry }: ShellFrameProps) {
  const copy = FAILURE_CLASS_COPY[failureClass]
  return (
    <m.div variants={fadeSlideUp} initial="hidden" animate="visible" className="mx-auto max-w-3xl">
      <Card className="border-t-2 border-t-signal-fail shadow-[8px_8px_0_var(--color-shadow)]">
        <CardBody className="flex flex-col gap-6 px-8 py-9">
          <BlameBadge blame={copy.blame} />

          <h1 className="max-w-[20ch] font-display text-[32px] font-extrabold uppercase leading-[1.04] tracking-tight text-ink-primary">
            {copy.headline}
          </h1>
          <p className="max-w-prose text-sm leading-relaxed text-ink-soft">
            You didn't cause it and nothing you did was lost. We know it's happening.
          </p>

          <PublishingStatus failureClass={failureClass} publisherStatus={publisherStatus} />

          <div className="flex flex-wrap items-center gap-3">
            {copy.retrySafe ? <Button onClick={onRetry}>Try again</Button> : null}
            <Button as="a" href="/published" variant="secondary">
              Go to published
            </Button>
            <span className="font-mono text-[11px] text-ink-secondary">
              {copy.retrySafe
                ? "Retrying is safe — it can't publish anything."
                : 'Publishing reverted to manual until you review it.'}
            </span>
          </div>

          <div className="border-t border-border-hairline pt-4">
            <ReferenceLine reference={reference} />
          </div>
        </CardBody>
      </Card>
    </m.div>
  )
}

interface StandaloneFrameProps {
  publisherStatus: PublisherStatus
  reference: string
  onRetry?: () => void
}

// Always the `auth` failure class — ErrorPageProps ties `variant: 'standalone'` to it at the
// type level (RAL-163: "standalone is only for pre-auth"), so this frame's copy never has to
// branch on failureClass/blame the way ShellFrame's does.
function StandaloneFrame({ publisherStatus, reference, onRetry }: StandaloneFrameProps) {
  const copy = FAILURE_CLASS_COPY[FailureClassEnum.enum.auth]
  const tone =
    publisherStatus.state === PublisherStatusStateEnum.enum.unknown
      ? PublishingToneEnum.enum.waiting
      : copy.publishingTone
  const line =
    publisherStatus.state === PublisherStatusStateEnum.enum.unknown
      ? "We can't confirm your campaigns' status from here."
      : `Your campaigns keep running — publishing is ${copy.publishingSummary}.`

  return (
    <m.div
      variants={fadeSlideUp}
      initial="hidden"
      animate="visible"
      className="mx-auto flex min-h-[420px] max-w-xl flex-col px-8 py-9"
    >
      <div className="mb-8 flex items-center gap-2.5">
        <svg
          viewBox="0 0 66 56"
          height={22}
          fill="none"
          role="img"
          aria-label="Rally"
          className="block shrink-0 text-ink-primary"
        >
          <rect x={0} y={19} width={8} height={18} fill="currentColor" />
          <rect x={14} y={12} width={8} height={32} fill="currentColor" />
          <rect x={28} y={0} width={10} height={56} className="fill-signal-oxide" />
          <rect x={44} y={12} width={8} height={32} fill="currentColor" />
          <rect x={58} y={19} width={8} height={18} fill="currentColor" />
        </svg>
        <span className="font-display text-xl font-extrabold uppercase leading-none tracking-tight text-ink-primary">
          Rally
        </span>
      </div>

      <BlameBadge blame={copy.blame} />

      <h1 className="mt-4.5 max-w-[18ch] font-display text-[30px] font-extrabold uppercase leading-[1.03] tracking-tight text-ink-primary">
        {copy.headline}
      </h1>
      <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-soft">
        We can't sign you in right now. This is on us, not on you or your TikTok account.
      </p>

      <div className={`mt-5 max-w-md border-l-2 py-0.5 pl-4 ${TONE_BORDER[tone]}`}>
        <p className="text-[13px] leading-relaxed text-ink-soft">
          <strong className={`font-semibold ${TONE_TEXT[tone]}`}>{line}</strong>
        </p>
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-3 pt-6">
        {copy.retrySafe ? <Button onClick={onRetry}>Try again</Button> : null}
        <ReferenceLine reference={reference} />
      </div>
    </m.div>
  )
}

interface ErrorPageCommonProps {
  publisherStatus: PublisherStatus
  reference: string
  onRetry?: () => void
}

// RAL-163: standalone is pre-auth only, so it's paired with the `auth` failure class at the type
// level rather than accepted as a free parameter — that's what closed findings about the
// standalone frame showing copy/retry-safety for a failure class it was never designed to carry.
export type ErrorPageProps =
  | ({ variant: typeof ErrorVariantEnum.enum.shell; failureClass: FailureClass } & ErrorPageCommonProps)
  | ({ variant: typeof ErrorVariantEnum.enum.standalone } & ErrorPageCommonProps)

// RAL-162/163: two frames sharing one fixed copy table (FAILURE_CLASS_COPY) so headline, blame,
// and retry-safety are never composed at runtime. `shell` renders into the main column with the
// sidebar/header left mounted by the caller (RootErrorBoundary-equivalent for route errors);
// `standalone` carries its own wordmark since there's no header.
export function ErrorPage(props: ErrorPageProps) {
  if (props.variant === ErrorVariantEnum.enum.standalone) {
    const { publisherStatus, reference, onRetry } = props
    return <StandaloneFrame publisherStatus={publisherStatus} reference={reference} onRetry={onRetry} />
  }
  const { failureClass, publisherStatus, reference, onRetry } = props
  return (
    <ShellFrame failureClass={failureClass} publisherStatus={publisherStatus} reference={reference} onRetry={onRetry} />
  )
}
